import React, {useState, useEffect} from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import {toggle} from 'opticks/lib/optimizely'
import {isNil, isEmpty, anyPass} from 'ramda'

import {getFirstAndLastInitials} from '@daedalus/core/src/user/business'
import {PersonIcon} from '../../../foundation/icons/PersonIcon'
import PersonBorderIcon from '../../../foundation/icons/PersonBorder'

import {UserType} from 'types/User'
import {ThemeType} from 'types/Theme'

export enum AvatarType {
  Picture = 'picture',
  Icon = 'icon'
}

type PropsType = {
  /** The size of the Avatar */
  size?: number
  /** The size of the Avatar icon */
  iconSize?: number
  /** Appearance of avatar */
  type: AvatarType
  /** User's picture */
  picture?: string
  /** User's name */
  name?: string
}

type UserPhotoFallbackType = {
  size: PropsType['size']
  name?: UserType['name']
}

type StandardAvatarType = {
  size: PropsType['size']
  iconSize: PropsType['iconSize']
}

type AvatarStylesType = {
  theme?: ThemeType
  size: PropsType['size']
}

const AvatarElement = styled('img')(
  ({size}: AvatarStylesType) => css`
    height: ${size}px;
    width: ${size}px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  `
)

const DefaultPictureWrapper = styled('div')(
  ({size, theme}: AvatarStylesType) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background-color: ${theme.colors.avatarDefaultPictureBg};
    color: ${theme.colors.avatarInitials};
  `
)

const AvatarBackground = styled('div')(
  ({size, theme}: AvatarStylesType) => css`
    width: ${size}px;
    height: ${size}px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    background-color: ${theme.colors.avatarBackground};
    border-radius: 50%;
    overflow: hidden;
  `
)

const StandardAvatar = ({size, iconSize}: StandardAvatarType) =>
  toggle(
    'dq82hd-desktop-map',
    <AvatarBackground size={size}>
      <PersonIcon size={iconSize || size - 8} />
    </AvatarBackground>,
    <PersonBorderIcon size={size} />
  )

const UserPhotoFallback = ({name, size}: UserPhotoFallbackType) => {
  const initials = getFirstAndLastInitials(name)

  return (
    <DefaultPictureWrapper data-id="ProfilePicture" size={size}>
      {initials}
    </DefaultPictureWrapper>
  )
}

export const getShowAvatar = ({type, name, picture}) =>
  type === AvatarType.Icon ||
  (anyPass([isNil, isEmpty])(name) && anyPass([isNil, isEmpty])(picture))

const Avatar = ({size, iconSize, picture, name, type}: PropsType) => {
  const [shouldShowFallbackPhoto, setShouldShowFallbackPhoto] = useState(false)
  const shouldShowAvatar = getShowAvatar({type, name, picture})

  useEffect(() => {
    if (!picture) return setShouldShowFallbackPhoto(true)

    setShouldShowFallbackPhoto(false)
    const image = new Image()
    image.addEventListener('error', () => setShouldShowFallbackPhoto(true))
    image.src = picture
  }, [picture])

  if (shouldShowAvatar) {
    return <StandardAvatar size={size} iconSize={iconSize} />
  }

  if (shouldShowFallbackPhoto) {
    return <UserPhotoFallback size={size} name={name} />
  }

  return (
    <AvatarElement
      data-id="ProfilePicture"
      src={picture}
      size={size}
      alt={name}
      title={name}
    />
  )
}

Avatar.defaultProps = {
  size: 32,
  type: AvatarType.Icon
}

export default Avatar
